import { useMemo } from 'react';
import { StyleSheet } from 'react-native';

import {
  SegmentedButtons,
  type SegmentedButtonsProps
} from '@/shared/components/segmented-button';
import { useAppTheme } from '@/styles/hooks/useAppTheme';
import type { AppTheme } from '@/styles/theme';

const makeStyles = (theme: AppTheme) =>
  StyleSheet.create({
    container: {
      padding: 3,
      backgroundColor: theme.colors.surfaceVariant,
      borderRadius: 26
    },
    button: {
      flexGrow: 1,
      flexShrink: 1,
      flexBasis: 0,
      minWidth: 0,
      borderWidth: 0,
      borderRadius: 32,
      marginHorizontal: 2
    },
    firstButton: {
      borderTopRightRadius: 32,
      borderBottomRightRadius: 32
    },
    lastButton: {
      borderTopLeftRadius: 32,
      borderBottomLeftRadius: 32
    },
    activeButton: {
      boxShadow: '0 1px 4px rgba(0, 0, 0, 0.12)',
      elevation: 2
    },
    label: {
      flexShrink: 1
    }
  });

export const PillSegmentedButtons = <T extends string>({
  buttons,
  style,
  ...rest
}: SegmentedButtonsProps<T>) => {
  const theme = useAppTheme();
  const styles = useMemo(() => makeStyles(theme), [theme]);

  const segmentedTheme = {
    roundness: 8,
    colors: {
      secondaryContainer: theme.colors.surfaceContainerLowest,
      onSecondaryContainer: theme.colors.primary,
      outline: 'transparent',
      surfaceDisabled: 'transparent'
    }
  };

  return (
    <SegmentedButtons<T>
      {...rest}
      style={[styles.container, style]}
      theme={segmentedTheme}
      rippleStyle={{ borderRadius: 32 }}
      buttons={buttons.map((button, index) => ({
        ...button,
        style: [
          styles.button,
          index === 0 && styles.firstButton,
          index === buttons.length - 1 && styles.lastButton,
          button.value === rest.value && styles.activeButton,
          button.style
        ],
        labelStyle: [styles.label, button.labelStyle]
      }))}
    />
  );
};
